import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '../common';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const BulkEmailModal = ({ isOpen, onClose, eventId, eventTitle }) => {
  const [enrolledStudents, setEnrolledStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [emailData, setEmailData] = useState({
    subject: '',
    message: '',
    sending: false,
    error: null,
    success: false
  });
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [htmlMode, setHtmlMode] = useState(false);
  const messageRef = useRef(null);
  const quillRef = useRef(null);

  // Rich text editor configuration
  const modules = {
    toolbar: {
      container: [
        ['bold', 'italic', 'underline'],
        [{'list': 'ordered'}, {'list': 'bullet'}],
        ['link', 'clean'],
        [{'color': []}, {'background': []}],
        [{'size': ['small', false, 'large']}]
      ],
    }
  };
  
  const formats = [
    'bold', 'italic', 'underline',
    'list', 'bullet',
    'link',
    'color', 'background',
    'size'
  ];

  // Fetch enrolled students and templates when modal opens
  useEffect(() => {
    if (isOpen && eventId) {
      fetchEnrolledStudents();
      fetchTemplates();
      // Reset form
      setEmailData({
        subject: `Update about ${eventTitle || 'your class'}`,
        message: '',
        sending: false,
        error: null,
        success: false
      });
      setSelectedRecipients([]);
      setSelectedTemplateId('');
      setHtmlMode(false);
    }
  }, [isOpen, eventId, eventTitle]);

  // Fetch enrolled students
  const fetchEnrolledStudents = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/calendar/${eventId}/enrolled-students`, {
        headers: { 'x-auth-token': token }
      });
      
      if (response.data.success) {
        setEnrolledStudents(response.data.enrolled_students);
        // Pre-select all students
        setSelectedRecipients(response.data.enrolled_students.map(student => student.lead_id));
      }
    } catch (error) {
      console.error('Error fetching enrolled students:', error);
      setEmailData(prev => ({ 
        ...prev, 
        error: 'Failed to load enrolled students' 
      }));
    } finally {
      setLoading(false);
    }
  };

  // Fetch email templates
  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/email/templates', {
        headers: { 'x-auth-token': token }
      });
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching email templates:', error);
    }
  };

  // Apply selected template
  const applyTemplate = (template) => {
    if (!template) return;
    
    setEmailData(prev => ({
      ...prev,
      subject: template.subject || '',
      message: template.body || ''
    }));
  };

  // Handle template selection
  const handleTemplateChange = (e) => {
    const templateId = e.target.value;
    setSelectedTemplateId(templateId);
    
    if (templateId) {
      const selectedTemplate = templates.find(t => t.id === parseInt(templateId));
      if (selectedTemplate) {
        applyTemplate(selectedTemplate);
      }
    }
  };

  // Toggle recipient selection
  const toggleRecipient = (leadId) => {
    setSelectedRecipients(prev => 
      prev.includes(leadId) 
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  // Select/deselect all recipients
  const toggleAllRecipients = () => {
    if (selectedRecipients.length === enrolledStudents.length) {
      setSelectedRecipients([]);
    } else {
      setSelectedRecipients(enrolledStudents.map(student => student.lead_id));
    }
  };

  // Send emails
  const handleSendEmails = async () => {
    if (selectedRecipients.length === 0) {
      setEmailData(prev => ({ ...prev, error: 'Please select at least one recipient' }));
      return;
    }

    if (!emailData.subject.trim()) {
      setEmailData(prev => ({ ...prev, error: 'Please enter a subject' }));
      return;
    }

    if (!emailData.message.trim()) {
      setEmailData(prev => ({ ...prev, error: 'Please enter a message' }));
      return;
    }

    try {
      setEmailData(prev => ({ ...prev, sending: true, error: null }));
      const token = localStorage.getItem('token');

      // Get selected students data
      const selectedStudents = enrolledStudents.filter(student => 
        selectedRecipients.includes(student.lead_id)
      );

      // Send individual emails to each selected parent
      const emailPromises = selectedStudents.map(student => {
        // Create text-only version by stripping HTML tags
        const textVersion = emailData.message.replace(/<[^>]*>?/gm, '');
        
        // Convert all newlines to <br> tags for proper HTML rendering
        const htmlVersion = emailData.message.replace(/\n/g, '<br>');

        return axios.post('/api/email/send', {
          to: student.lead_email,
          cc: student.lead_secondary_email || undefined,
          subject: emailData.subject,
          text: textVersion,
          html: htmlVersion,
          leadId: student.lead_id
        }, {
          headers: {
            'Content-Type': 'application/json',
            'x-auth-token': token
          }
        });
      });

      await Promise.all(emailPromises);

      setEmailData(prev => ({ 
        ...prev, 
        sending: false, 
        success: true,
        error: null
      }));

      // Close modal after 2 seconds
      setTimeout(() => {
        onClose(true); // Pass true to indicate success
      }, 2000);

    } catch (error) {
      console.error('Error sending emails:', error);
      setEmailData(prev => ({ 
        ...prev, 
        sending: false, 
        error: 'Failed to send some emails. Please try again.' 
      }));
    }
  };

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEmailData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex justify-center items-center">
      <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full m-4 p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            Send Email to Parents - {eventTitle}
          </h3>
          <button 
            onClick={() => onClose()}
            className="text-gray-400 hover:text-gray-500 focus:outline-none"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {emailData.error && (
          <div className="mb-4 p-3 bg-red-50 text-red-700 border border-red-200 rounded">
            {emailData.error}
          </div>
        )}

        {emailData.success && (
          <div className="mb-4 p-3 bg-green-50 text-green-700 border border-green-200 rounded flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            Emails sent successfully to {selectedRecipients.length} parent(s)!
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Recipients Section */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Recipients ({selectedRecipients.length} of {enrolledStudents.length} selected)
                </label>
                <button
                  type="button"
                  onClick={toggleAllRecipients}
                  className="text-sm text-indigo-600 hover:text-indigo-800"
                >
                  {selectedRecipients.length === enrolledStudents.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              <div className="max-h-32 overflow-y-auto border border-gray-300 rounded-md p-2 bg-gray-50">
                {enrolledStudents.map((student) => (
                  <label key={student.lead_id} className="flex items-center space-x-2 py-1">
                    <input
                      type="checkbox"
                      checked={selectedRecipients.includes(student.lead_id)}
                      onChange={() => toggleRecipient(student.lead_id)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <span className="text-sm">
                      {student.lead_name} ({student.lead_email})
                      {student.student_name && ` - ${student.student_name}`}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Template Selection */}
            <div>
              <label htmlFor="template" className="block text-sm font-medium text-gray-700 mb-1">
                Email Template (Optional)
              </label>
              <select
                id="template"
                value={selectedTemplateId}
                onChange={handleTemplateChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                disabled={emailData.sending}
              >
                <option value="">-- Select a template --</option>
                {templates.map(template => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Subject */}
            <div>
              <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                Subject
              </label>
              <input
                type="text"
                id="subject"
                name="subject"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                value={emailData.subject}
                onChange={handleChange}
                disabled={emailData.sending}
                required
              />
            </div>

            {/* Message */}
            <div>
              <div className="flex justify-between items-center mb-1">
                <label htmlFor="message" className="block text-sm font-medium text-gray-700">
                  Message
                </label>
                <div className="bg-gray-100 rounded-md p-0.5 flex items-center">
                  <button
                    type="button"
                    onClick={() => setHtmlMode(false)}
                    className={`text-xs px-3 py-1 rounded-md ${!htmlMode ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-700'}`}
                  >
                    Rich Text
                  </button>
                  <button
                    type="button"
                    onClick={() => setHtmlMode(true)}
                    className={`text-xs px-3 py-1 rounded-md ${htmlMode ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-700'}`}
                  >
                    HTML
                  </button>
                </div>
              </div>
              
              {htmlMode ? (
                <textarea
                  id="message"
                  name="message"
                  rows={8}
                  ref={messageRef}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 font-mono"
                  value={emailData.message}
                  onChange={handleChange}
                  disabled={emailData.sending}
                  required
                />
              ) : (
                <div className="border border-gray-300 rounded-md overflow-hidden">
                  <ReactQuill
                    ref={quillRef}
                    value={emailData.message}
                    onChange={(content) => setEmailData(prev => ({ ...prev, message: content }))}
                    modules={modules}
                    formats={formats}
                    placeholder="Type your message here..."
                    className="bg-white rounded-md"
                    theme="snow"
                  />
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => onClose()}
                disabled={emailData.sending}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={handleSendEmails}
                disabled={emailData.sending || selectedRecipients.length === 0}
                isLoading={emailData.sending}
              >
                {emailData.sending ? 'Sending...' : `Send to ${selectedRecipients.length} Parent(s)`}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkEmailModal;